// Wraps markerjs2 in a promise. Loads lazily so the editor only ships when
// the user actually annotates a screenshot. The library renders its UI as a
// fullscreen popup attached to <body>, so we host the source <img> in light
// DOM (offscreen) rather than inside the widget's shadow root.

const dataURLToFile = async (dataUrl: string, name: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], name, { type: blob.type || "image/png" });
};

export const annotateImage = async (file: File): Promise<File> => {
  const { MarkerArea } = await import("markerjs2");

  const url = URL.createObjectURL(file);
  const img = document.createElement("img");
  img.src = url;
  img.style.position = "fixed";
  img.style.left = "-99999px";
  img.style.top = "0";
  img.style.visibility = "hidden";
  document.body.appendChild(img);

  await new Promise<void>((resolve, reject) => {
    if (img.complete && img.naturalWidth > 0) return resolve();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Failed to load screenshot for annotation"));
  });

  return new Promise<File>((resolve, reject) => {
    const ma = new MarkerArea(img);
    ma.settings.displayMode = "popup";

    let pendingDataUrl: string | null = null;
    let cleaned = false;

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(url);
      img.remove();
    };

    // Capture the dataUrl synchronously here — the async fetch/blob
    // conversion runs from the close handler so the promise can't resolve
    // with a stale value.
    ma.addEventListener("render", (ev) => {
      pendingDataUrl = ev.dataUrl;
    });

    ma.addEventListener("close", async () => {
      cleanup();
      if (!pendingDataUrl) {
        resolve(file);
        return;
      }
      try {
        resolve(await dataURLToFile(pendingDataUrl, file.name));
      } catch (err) {
        reject(err);
      }
    });

    ma.show();
  });
};
